/**
 * Grouped, searchable, virtualized change browser (WHA-3695 follow-up).
 * Replaces the flat list. Store-agnostic — the 3D rail and the standalone
 * overview modal both render it, wiring the callbacks to their own host.
 *
 * Layout: group-by switcher + search + add/remove/modify visibility chips, then
 * a virtualized flattened tree of group-header rows and (when expanded) element
 * rows. Clicking a group header expands it AND highlights its members in 3D
 * (when `onSelectGroup` is provided); clicking an element selects it.
 */

import { useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { cn } from "@/lib/utils";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { BuildingIfcViewerSections } from "@/lib/i18n/types/buildings-sections/ifc-viewer";
import type { IfcChangeType, IfcElementChange, IfcStoreyRef } from "@/stores/ifcModel/repository";
import { classLabel, disciplineIncludedClassLabels } from "../disciplineMap";
import { groupChanges, type DiffGroup, type GroupByMode } from "./groupChanges";

type Category = IfcChangeType;

const GROUP_HEIGHT = 40;
const ROW_HEIGHT = 44;

const CATEGORY_STYLE: Record<
  IfcChangeType | "unchanged",
  { dot: string; text: string; bar: string; chipActive: string }
> = {
  added: {
    dot: "bg-emerald-500",
    text: "text-emerald-600 dark:text-emerald-400",
    bar: "bg-emerald-500",
    chipActive: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  },
  removed: {
    dot: "bg-rose-500",
    text: "text-rose-600 dark:text-rose-400",
    bar: "bg-rose-500",
    chipActive: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  },
  modified: {
    dot: "bg-amber-500",
    text: "text-amber-600 dark:text-amber-400",
    bar: "bg-amber-500",
    chipActive: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  },
  unchanged: {
    dot: "bg-slate-400",
    text: "text-muted-foreground",
    bar: "bg-slate-400",
    chipActive: "bg-muted text-foreground",
  },
};

type FlatRow =
  | { kind: "group"; group: DiffGroup; expanded: boolean }
  | { kind: "element"; change: IfcElementChange };

interface DiffVisibility {
  added: boolean;
  removed: boolean;
  modified: boolean;
}

interface IfcDiffBrowserProps {
  changes: IfcElementChange[];
  storeys: IfcStoreyRef[];
  visibility: DiffVisibility;
  onToggleVisibility: (category: Category) => void;
  selectedGuid: string | null;
  onSelect: (guid: string | null) => void;
  /** 3D-only: highlight + frame every member of a group. Omitted in the modal. */
  onSelectGroup?: (guids: string[]) => void;
  /** WHA-3695 cont.5: whether a changed element has geometry to show in 3D.
   * Omitted (⇒ always drawable) in the modal, which has no live scene. */
  isDrawable?: (guid: string) => boolean;
  language: "en" | "et";
  tv: BuildingIfcViewerSections;
  /** Presentation variant: "rail" (default) keeps the 3D rail's compact
   * controls and on/off visibility toggles; "modal" renders roomier controls
   * (segmented group-by, larger search) for the overview modal, defaults the
   * grouping to storey, and treats the change-type chips as FILTERS — none
   * selected shows everything, selecting chips narrows to those types. */
  variant?: "rail" | "modal";
}

const GROUP_MODES: {
  mode: GroupByMode;
  labelKey: "groupByClass" | "groupByStorey" | "groupByDiscipline";
}[] = [
  { mode: "class", labelKey: "groupByClass" },
  { mode: "storey", labelKey: "groupByStorey" },
  { mode: "discipline", labelKey: "groupByDiscipline" },
];

/** Modal ordering — storey leads (it's also the modal's default grouping). */
const MODAL_GROUP_MODES: typeof GROUP_MODES = [
  { mode: "storey", labelKey: "groupByStorey" },
  { mode: "class", labelKey: "groupByClass" },
  { mode: "discipline", labelKey: "groupByDiscipline" },
];

export function IfcDiffBrowser({
  changes,
  storeys,
  visibility,
  onToggleVisibility,
  selectedGuid,
  onSelect,
  onSelectGroup,
  isDrawable,
  language,
  tv,
  variant = "rail",
}: IfcDiffBrowserProps) {
  const isModal = variant === "modal";
  // Storeys are the natural first cut when reviewing model changes, so the
  // modal leads with them; the 3D rail keeps its historical class default.
  const [groupBy, setGroupBy] = useState<GroupByMode>(isModal ? "storey" : "class");
  const [search, setSearch] = useState("");
  // Debounce the term that drives the (expensive) re-group, while the input
  // stays fully responsive on `search` (WHA-3695 perf).
  const debouncedSearch = useDebouncedValue(search, 200);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  // In the modal the chips are filters: none selected means "show everything";
  // the rail's chips stay on/off visibility toggles (all on by default).
  const noFilterActive =
    isModal && !visibility.added && !visibility.removed && !visibility.modified;

  // Filter by visibility + search, then group.
  const groups = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const filtered = changes.filter((c) => {
      if (!noFilterActive && !visibility[c.change_type]) return false;
      if (!q) return true;
      return (
        (c.name?.toLowerCase().includes(q) ?? false) ||
        classLabel(c.ifc_class).toLowerCase().includes(q) ||
        c.ifc_class.toLowerCase().includes(q)
      );
    });
    return groupChanges(filtered, groupBy, {
      language,
      storeys,
      unassignedLabel: tv.unassignedStorey,
    });
  }, [
    changes,
    visibility,
    noFilterActive,
    debouncedSearch,
    groupBy,
    storeys,
    language,
    tv.unassignedStorey,
  ]);

  const shownCount = useMemo(() => groups.reduce((n, g) => n + g.total, 0), [groups]);

  // Flatten groups → rows for the virtualizer.
  const rows = useMemo<FlatRow[]>(() => {
    const out: FlatRow[] = [];
    for (const g of groups) {
      const isExp = expanded.has(g.key);
      out.push({ kind: "group", group: g, expanded: isExp });
      if (isExp) for (const change of g.elements) out.push({ kind: "element", change });
    }
    return out;
  }, [groups, expanded]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (rows[i].kind === "group" ? GROUP_HEIGHT : ROW_HEIGHT),
    overscan: 10,
  });

  const toggleGroup = (group: DiffGroup) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(group.key)) next.delete(group.key);
      else next.add(group.key);
      return next;
    });
    onSelectGroup?.(group.guids);
  };

  const CATEGORY_CHIPS: Category[] = ["added", "removed", "modified"];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Controls */}
      <div className={cn("flex flex-col", isModal ? "gap-2.5 px-4 py-3" : "gap-2 px-3 py-2")}>
        {/* Group-by switcher */}
        {isModal ? (
          <div className="flex items-center gap-3">
            {/* Search (modal: shares the row with the segmented group-by) */}
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tv.diffSearchPlaceholder}
                className="w-full rounded-md bg-muted/50 py-1.5 pl-8 pr-3 text-sm outline-none transition-colors focus:bg-background focus:ring-1 focus:ring-ring"
              />
            </div>
            {/*
              No explicit JSX type argument here (T is inferred from `groupBy`):
              lovable-tagger (dev-mode vite plugin) inserts its attributes right
              after the component name, so `<SegmentedControl<GroupByMode>`
              becomes invalid syntax and the dev server / e2e job cannot compile.
            */}
            <SegmentedControl
              value={groupBy}
              onValueChange={(value) => {
                const selected = MODAL_GROUP_MODES.find((g) => g.mode === value);
                if (selected) setGroupBy(selected.mode);
              }}
              options={MODAL_GROUP_MODES.map((g) => ({ value: g.mode, label: tv[g.labelKey] }))}
              className="h-8 shrink-0"
            />
          </div>
        ) : (
          <div className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">{tv.groupByLabel}:</span>
            {GROUP_MODES.map((g) => (
              <button
                key={g.mode}
                type="button"
                onClick={() => setGroupBy(g.mode)}
                className={cn(
                  "rounded px-1.5 py-0.5 transition-colors",
                  groupBy === g.mode
                    ? "bg-muted font-medium"
                    : "text-muted-foreground hover:bg-muted/50"
                )}
              >
                {tv[g.labelKey]}
              </button>
            ))}
          </div>
        )}

        {/* Search (rail: its own row) */}
        {!isModal && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tv.diffSearchPlaceholder}
              className="w-full rounded border border-border bg-transparent py-1 pl-7 pr-2 text-xs outline-none focus:border-ring"
            />
          </div>
        )}

        {/* Change-type chips + count. Modal: filter pills (none active = show
            all). Rail: on/off visibility toggles, unchanged. */}
        <div className={cn("flex items-center", isModal ? "gap-2" : "gap-1.5")}>
          {CATEGORY_CHIPS.map((c) => {
            const style = CATEGORY_STYLE[c];
            const active = visibility[c];
            return (
              <button
                key={c}
                type="button"
                onClick={() => onToggleVisibility(c)}
                aria-pressed={active}
                className={
                  isModal
                    ? cn(
                        "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-[transform,background-color,color] duration-150 hover:scale-105",
                        active ? style.chipActive : "text-muted-foreground"
                      )
                    : cn(
                        "flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] transition-colors",
                        active ? "border-border bg-muted/40" : "border-transparent opacity-50"
                      )
                }
              >
                <span className={cn("size-2 rounded-full", style.dot)} />
                {labelFor(c, tv)}
              </button>
            );
          })}
          <span
            className={cn("ml-auto text-muted-foreground", isModal ? "text-[11px]" : "text-[10px]")}
          >
            {tv.diffShowingOf
              .replace("{shown}", String(shownCount))
              .replace("{total}", String(changes.length))}
          </span>
        </div>
      </div>

      {/* Virtualized grouped list */}
      <TooltipProvider delayDuration={200}>
        <div
          ref={scrollRef}
          className={cn(
            "ifc-glass-scroll min-h-0 flex-1 overflow-y-auto",
            !isModal && "border-t border-border"
          )}
        >
          <div style={{ height: virtualizer.getTotalSize(), position: "relative", width: "100%" }}>
            {virtualizer.getVirtualItems().map((vRow) => {
              const row = rows[vRow.index];
              return (
                <div
                  key={vRow.key}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: vRow.size,
                    transform: `translateY(${vRow.start}px)`,
                  }}
                >
                  {row.kind === "group" ? (
                    <GroupRow
                      group={row.group}
                      expanded={row.expanded}
                      onToggle={() => toggleGroup(row.group)}
                      modal={isModal}
                      groupBy={groupBy}
                      tv={tv}
                    />
                  ) : (
                    <ElementRow
                      change={row.change}
                      selected={selectedGuid === row.change.guid}
                      drawable={isDrawable ? isDrawable(row.change.guid) : true}
                      onClick={() =>
                        onSelect(selectedGuid === row.change.guid ? null : row.change.guid)
                      }
                      tv={tv}
                      modal={isModal}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </TooltipProvider>
    </div>
  );
}

function GroupRow({
  group,
  expanded,
  onToggle,
  modal,
  groupBy,
  tv,
}: {
  group: DiffGroup;
  expanded: boolean;
  onToggle: () => void;
  modal?: boolean;
  groupBy: GroupByMode;
  tv: BuildingIfcViewerSections;
}) {
  const total = group.total || 1;
  // In discipline mode, explain which element types the discipline covers —
  // group.key is the discipline key (AR/EK/…/Lift/OTHER).
  const disciplineHint = groupBy === "discipline" ? disciplineTooltip(group.key, tv) : null;
  const labelSpan = <span className="truncate text-xs font-medium">{group.label}</span>;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        "flex h-full w-full items-center gap-1.5 px-2 text-left",
        modal
          ? "origin-left transition-transform duration-150 hover:scale-[1.01]"
          : "border-b border-border/50 transition-colors hover:bg-muted/40"
      )}
    >
      {expanded ? (
        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
      ) : (
        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
      )}
      {group.color && (
        <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
      )}
      {disciplineHint ? (
        <Tooltip>
          <TooltipTrigger asChild>{labelSpan}</TooltipTrigger>
          <TooltipContent variant="dark" side="right" className="max-w-xs">
            {disciplineHint}
          </TooltipContent>
        </Tooltip>
      ) : (
        labelSpan
      )}
      {/* mini proportion bar */}
      <span className="ml-auto flex h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted">
        {(["added", "removed", "modified"] as const).map((c) =>
          group.counts[c] > 0 ? (
            <span
              key={c}
              className={CATEGORY_STYLE[c].bar}
              style={{ width: `${(group.counts[c] / total) * 100}%` }}
            />
          ) : null
        )}
      </span>
      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
        {group.counts.added > 0 && (
          <span className={CATEGORY_STYLE.added.text}>+{group.counts.added} </span>
        )}
        {group.counts.removed > 0 && (
          <span className={CATEGORY_STYLE.removed.text}>−{group.counts.removed} </span>
        )}
        {group.counts.modified > 0 && (
          <span className={CATEGORY_STYLE.modified.text}>~{group.counts.modified}</span>
        )}
      </span>
    </button>
  );
}

function ElementRow({
  change,
  selected,
  drawable,
  onClick,
  tv,
  modal,
}: {
  change: IfcElementChange;
  selected: boolean;
  drawable: boolean;
  onClick: () => void;
  tv: BuildingIfcViewerSections;
  modal?: boolean;
}) {
  const style = CATEGORY_STYLE[change.change_type];
  const badges: string[] = [];
  if (change.change_type === "modified") {
    if (change.prop_changed) badges.push(tv.changedProperty);
    if (change.material_changed) badges.push(tv.changedMaterial);
    if (change.attr_changed) badges.push(tv.changedAttribute);
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-full w-full flex-col justify-center gap-0.5 pl-7 pr-3 text-left",
        modal
          ? "origin-left transition-transform duration-150 hover:scale-[1.01]"
          : "border-b border-border/40 transition-colors hover:bg-muted/40",
        selected && (modal ? "rounded-md bg-muted/60" : "bg-muted/60"),
        // Non-geometric change (void / port / bodyless element): dim it so the
        // row reads as "can't appear in 3D" rather than missing (WHA-3695 cont.5).
        !drawable && "opacity-55"
      )}
    >
      <div className="flex items-center gap-1.5">
        <span className={cn("size-2 shrink-0 rounded-full", style.dot)} />
        <span className="truncate text-xs">{change.name ?? classLabel(change.ifc_class)}</span>
        <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
          {classLabel(change.ifc_class)}
        </span>
      </div>
      {(!drawable || badges.length > 0) && (
        <div className="flex flex-wrap items-center gap-1 pl-3.5">
          {!drawable && (
            <span className="rounded border border-border bg-muted/60 px-1 text-[9px] uppercase tracking-wide text-muted-foreground">
              {tv.diffNotIn3d}
            </span>
          )}
          {badges.map((b) => (
            <span
              key={b}
              className="rounded bg-muted px-1 text-[9px] uppercase tracking-wide text-muted-foreground"
            >
              {b}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

/** Tooltip text for a discipline group: the element types it covers, or the
 * catch-all hint for OTHER. Null when there's nothing useful to say. */
function disciplineTooltip(key: string, tv: BuildingIfcViewerSections): string | null {
  const classes = disciplineIncludedClassLabels(key);
  if (classes.length === 0) return tv.disciplineOtherHint;
  return `${tv.disciplineIncludes}: ${classes.join(", ")}`;
}

function labelFor(c: IfcChangeType, tv: BuildingIfcViewerSections): string {
  switch (c) {
    case "added":
      return tv.changeAdded;
    case "removed":
      return tv.changeRemoved;
    default:
      return tv.changeModified;
  }
}
